import { useAuth } from "context/AuthContext";
import { useSelectedTimings } from "context/TimingContext";
import useEmblaCarousel from "embla-carousel-react";
import { useTeacherSlots } from "hooks/useTeacherSlots";
import moment from "moment";
import { userRoute } from "pages/user/user.routes";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { LoginModal } from "ui/blocks/AuthModals/loginModal/LoginModal";
import { SignupModal } from "ui/blocks/AuthModals/singupModal/SignupModal";
import { mediaSizes } from "ui/theme";
import {
    formatMinutesToHours,
    getClassType,
    getHighestDiscount,
    getLowestValidPrice,
    USDollar,
} from "utils";

const ChevronIcon = ({ open }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
    >
        <path d="M6 9L12 15L18 9" stroke="#1d1d1f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const MobileScheduleCard = ({ classDetail }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token } = useAuth();
    const { handleAddSelectedTiming } = useSelectedTimings();
    const [emblaRef] = useEmblaCarousel({ align: "start" });
    const [showDiscount, setShowDiscount] = useState(false);
    const [showSignupModal, setShowSignupModal] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);


    const teacherInfo = classDetail?.teacher;
    const hasOnlineClasses = !!teacherInfo?.online_slots?.length;
    const defaultOnlineType = getClassType(teacherInfo?.online_type, hasOnlineClasses);
    const online_type = state?.online_type || defaultOnlineType;
    const isOnline = online_type === "online" && hasOnlineClasses;

    const { availableTimings } = useTeacherSlots({
        teacherInfo,
        online_type: isOnline ? "online" : "offline",
    });

    const durationInHours = formatMinutesToHours(teacherInfo?.class_duration);
    const lowestPrice = isOnline
        ? teacherInfo?.price_per_online_class
        : getLowestValidPrice(
              teacherInfo?.type_of_class,
              teacherInfo?.price_per_group_class,
              teacherInfo?.price_per_private_class
          );

    const discountList = teacherInfo?.discount_policy_for_private_class || [];
    const highestDiscount = getHighestDiscount(teacherInfo);

    const handleSelectDate = (timing) => {
        handleAddSelectedTiming(timing);
        navigate(userRoute.paymentPage + classDetail?.id);
    };

    const requireAuth = (cb) => {
        if (!token) { setShowSignupModal(true); return; }
        cb();
    };

    if (!classDetail) return null;

    return (
        <>
        <Wrapper>
            {showSignupModal && (
                <SignupModal
                    show={showSignupModal}
                    handleClose={() => setShowSignupModal(false)}
                    setLoginModal={setShowLoginModal}
                />
            )}
            {showLoginModal && (
                <LoginModal
                    show={showLoginModal}
                    handleClose={() => setShowLoginModal(false)}
                    setSignupModal={setShowSignupModal}
                    hasHelperText={false}
                />
            )}

            <Title>Choose from available dates</Title>
            <Subtitle>from ${lowestPrice}/ {durationInHours}</Subtitle>

            <EmblaViewport ref={emblaRef}>
                <EmblaContainer>
                    {availableTimings?.slice(0, 6).map((timing) => {
                        const fullDate = moment(timing.date).format("MMMM D, YYYY");
                        const cleanTime = moment(timing.time, "hh:mm A").format("h:mm A");
                        const cost = timing.cost
                            ? USDollar.format(Number(String(timing.cost).replace("$", "").trim()))
                            : "";

                        return (
                            <EmblaSlide key={timing.id}>
                                <SlotCard>
                                    <SlotRow>
                                        <SlotLabel>DATE</SlotLabel>
                                        <SlotValue>{fullDate}</SlotValue>
                                    </SlotRow>
                                    <SlotRow>
                                        <SlotLabel>TIME</SlotLabel>
                                        <SlotValue>{cleanTime}</SlotValue>
                                    </SlotRow>
                                    <SlotRow>
                                        <SlotLabel>COST</SlotLabel>
                                        <SlotValue>{cost}</SlotValue>
                                    </SlotRow>
                                    <ChooseBtn onClick={() => requireAuth(() => handleSelectDate(timing))}>
                                        Choose
                                    </ChooseBtn>
                                </SlotCard>
                            </EmblaSlide>
                        );
                    })}
                </EmblaContainer>
            </EmblaViewport>

            {discountList.length > 0 && (
                <>
                    <DiscountRow onClick={() => setShowDiscount((p) => !p)}>
                        <span>Discount Offered</span>
                        <ChevronIcon open={showDiscount} />
                    </DiscountRow>
                    <DiscountDivider />
                    {showDiscount && (
                        <DiscountList>
                            {discountList.map((d, i) => (
                                <DiscountItem key={i}>
                                    {d.policy} — {d.percentage}% off
                                </DiscountItem>
                            ))}
                        </DiscountList>
                    )}
                </>
            )}

            {highestDiscount && !discountList.length && (
                <>
                    <DiscountRow onClick={() => setShowDiscount((p) => !p)}>
                        <span>Discount Offered</span>
                        <ChevronIcon open={showDiscount} />
                    </DiscountRow>
                    <DiscountDivider />
                </>
            )}

            <div>
                <ShowMoreBtn onClick={() => requireAuth(() => navigate(userRoute.showMoreDates + classDetail?.id))}>
                    Show More Dates
                </ShowMoreBtn>
            </div>
        </Wrapper>

        <FloatingBtn visible onClick={() => requireAuth(() => navigate(userRoute.showMoreDates + classDetail?.id))}>
            Book Class
        </FloatingBtn>
        </>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-family: Poppins, sans-serif;
    padding-bottom: 2.5rem;

    @media (width >= ${mediaSizes.lg}) {
        display: none;
    }
`;

const Title = styled.h2`
    font-family: Poppins, sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: #1d1d1f;
    margin: 0;
`;

const Subtitle = styled.p`
    font-family: Poppins, sans-serif;
    font-size: 14px;
    color: rgba(29, 29, 31, 0.6);
    margin: -0.5rem 0 0;
`;

const EmblaViewport = styled.div`
    overflow: hidden;
`;

const EmblaContainer = styled.div`
    display: flex;
    touch-action: pan-y pinch-zoom;
    margin-left: calc(0.75rem * -1);
`;

const EmblaSlide = styled.div`
    flex: 0 0 240px;
    min-width: 0;
    padding-left: 0.75rem;

    @media (width >= ${mediaSizes.md}) {
        flex: 0 0 280px;
    }
`;

const SlotCard = styled.div`
    background: #f7f5f1;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const SlotRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SlotLabel = styled.span`
    font-family: Poppins, sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(29, 29, 31, 0.45);
    width: 40px;
    flex-shrink: 0;
`;

const SlotValue = styled.span`
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #1d1d1f;
    white-space: nowrap;
`;

const ChooseBtn = styled.button`
    all: unset;
    cursor: pointer;
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1d1d1f;
    text-decoration: underline;
    text-underline-offset: 3px;
    margin-top: 4px;

    &:hover {
        opacity: 0.7;
    }
`;

const DiscountRow = styled.button`
    all: unset;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Poppins, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1d1d1f;
    padding-block: 4px;
`;

const DiscountDivider = styled.div`
    height: 1px;
    background: rgba(29, 29, 31, 0.1);
    margin-top: -0.5rem;
`;

const DiscountList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const DiscountItem = styled.p`
    font-family: Poppins, sans-serif;
    font-size: 13px;
    color: rgba(29, 29, 31, 0.75);
    margin: 0;
`;

const FloatingBtn = styled.button`
    all: unset;
    cursor: pointer;
    position: fixed;
    bottom: 1.25rem;
    left: 1.3rem;
    right: 1.3rem;
    padding: 13px;
    background: #1d1d1f;
    color: #ffffff;
    font-family: Poppins, sans-serif;
    font-size: 15px;
    font-weight: 500;
    border-radius: 100px;
    text-align: center;
    box-sizing: border-box;
    z-index: 50;
    transition: opacity 0.25s ease, transform 0.25s ease;
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    pointer-events: ${({ visible }) => (visible ? "auto" : "none")};
    transform: ${({ visible }) => (visible ? "translateY(0)" : "translateY(12px)")};

    @media (width >= ${mediaSizes.lg}) {
        display: none;
    }
`;

const ShowMoreBtn = styled.button`
    all: unset;
    cursor: pointer;
    width: 100%;
    padding: 13px;
    background: #1d1d1f;
    color: #ffffff;
    font-family: Poppins, sans-serif;
    font-size: 15px;
    font-weight: 500;
    border-radius: 100px;
    text-align: center;
    box-sizing: border-box;
    transition: background 0.2s ease;

    &:hover {
        background: #2d2d2f;
    }
`;
